import { useDispatch, useSelector } from "react-redux";
import { setDraggingComponent } from "@/store/iframeSlice";
import {
  CursorType,
  ModalType,
  setCursorType,
  setModal,
  setSelectedLayoutOption,
} from "@/store/globalSettingsSlice";
import React, { useMemo } from "react";
import useLayoutPresets, {
  LayoutId,
  LayoutPreset,
} from "@/components/AppHeader/hooks/useLayoutPresets";
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Check } from "lucide-react";
import { RootState } from "@/store/store";
import { createDummyComponent } from "@/components/AppHeader/utils";
import { useActiveProject } from "@/utils/utils";
import { setTempoClipboardDataOnDrag } from "@/components/Canvas/utils/canvasClipboardData";
import { ReactFramework } from "@/utils/templateProjects";

const FrameOptions = React.memo(() => {
  const { options } = useLayoutPresets();
  const dispatch = useDispatch();

  const selectedLayoutOption = useSelector(
    (state: RootState) => state.globalSettings.selectedLayoutOption,
  );

  const project = useActiveProject();

  const selectedLayoutOptionToUse = useMemo(() => {
    return selectedLayoutOption || options[0];
  }, [selectedLayoutOption, options]);

  const handleOnDragStart = (e, option: LayoutPreset) => {
    e.dataTransfer.dropEffect = "move";

    dispatch(setDraggingComponent("layout"));

    let fileContents = "";
    let htmlForInstantUpdate = "";
    if (project.framework === ReactFramework.EXPO) {
      fileContents = createDummyComponent(
        option.getExpoJsx!(""),
        project.framework,
      );
      htmlForInstantUpdate = option.getHtmlWithCustomStyles("");
    } else {
      fileContents = createDummyComponent(
        option.getJsxWithCustomClasses!(""),
        project.framework,
      );
      htmlForInstantUpdate = option.getHtmlWithCustomStyles("");
    }

    setTempoClipboardDataOnDrag(e, {
      htmlForInstantUpdate,
      fileContents,
      insertIntoHoveredElement: true,
    });
  };

  const handleOnClick = (option: LayoutPreset) => {
    dispatch(setSelectedLayoutOption(option));
  };

  return (
    <DropdownMenuContent
      onClick={() => {
        dispatch(
          setCursorType({
            cursorType: CursorType.Draw,
          }),
        );
      }}
      className="w-[250px]"
    >
      {options.map((option) => {
        const IconComponent = option.icon;
        return (
          <DropdownMenuItem
            key={option.id}
            className="cursor-grab p-0.5 text-nowrap text-xs font-semibold flex items-center flex-row gap-2"
            draggable={option.id !== LayoutId.APPLICATION}
            onDragStart={(e) => handleOnDragStart(e, option)}
            onDragEnd={() => {
              dispatch(setDraggingComponent(false));
              dispatch(
                setCursorType({
                  cursorType: CursorType.Pointer,
                }),
              );
              dispatch(setModal(ModalType.None));
            }}
            onClick={() => handleOnClick(option)}
          >
            <div className="bg-secondary-200 size-11 min-w-11 rounded-md flex justify-center items-center">
              <IconComponent />
            </div>
            <div className="flex items-center justify-between w-full pr-2">
              {option.name}
              {option.id === selectedLayoutOptionToUse.id && (
                <Check size="14" />
              )}
            </div>
          </DropdownMenuItem>
        );
      })}
    </DropdownMenuContent>
  );
});

export default FrameOptions;
